import {
  refreshPipeline,
  resetPageNumbers,
  setContextualSupport,
  setCurrentPage,
  setDetailViewCard,
  setSortCriteria,
  resetSortCriteria,
  setTotalItems,
  setTotalPages,
  unsetContextualSupport,
  updateSearchFilters,
  getCredentialsGroups,
} from 'js/actions';
import {
  CLONE_FLOW_REQUEST,
  DELETE_PIPELINE_REQUEST,
  GET_MCP_FLOWS_REQUEST,
  REFRESH_PIPELINE_REQUEST,
  REMOVE_FLOW_FROM_PROJECT_REQUEST,
} from 'js/constants/actionTypes';
import { SearchFilterViews } from 'js/constants/visibilityFilters';
import { getConnectionOrVendorType } from 'js/lib/ResourceUtils';
import { has } from 'lodash';
import { ROUTE_AI_MCP_FLOWS } from 'new/constants/pathnames';
import { ModalFilePreview } from 'new/elements/ModalFilePreview';
import { ModalMoveToProject } from 'new/elements/ModalMoveToProject';
import { ModalReingest } from 'new/elements/ModalReingest';
import { ModalAddToMarketplace } from 'new/elements/ModalAddToMarketplace/ModalAddToMarketplace';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import useMetricsAdapter from 'new/state/metrics/adapter/useMetricsAdapter';
import Pipelines from 'js/components/pipelines/Pipelines';
import { saveState } from 'new/state/hashedState/action';
import { memoizedMCPFlowsSelector } from 'js/reducers/entities/selectors';
import { getFlowMetricsInterval } from 'js/reducers/metrics/selectors';
import { loadMCPFlows, loadMCPFlowsSearchSuggestions } from 'new/state/mcpFlows/mcpFlowsActions';
import type { ComponentType } from 'react';

interface MCPFlowsState {
  activeViews: unknown;
  accessControl: unknown;
  selectedFlow: unknown;
  useExpanded: unknown;
  pipelines: Array<Record<string, unknown>>;
  filters: unknown[];
  rootNodeKeys: unknown;
  flowNodes: Record<string, unknown>;
  listingPageForRequest: number;
  currentPage: number;
  sortCriteria: string;
  lastAddedSourceId: string;
  lastAddedDataSetId: string;
  lastAddedSinkId: string;
  getSourceMeta: (flowNodeId: string) => unknown;
  entities: Record<string, unknown>;
  dataSetSchemas: unknown;
  lastLoaded: unknown;
  isLoadingPipelines: boolean;
  isLoadingProjectById: boolean;
  loadingRemoveFromProject: boolean;
  loadingDeleteProject: boolean;
  isRefreshingPipeline: boolean;
  selectedCardID: string | null;
  projectSelectedFlow: unknown;
  projectDataFlowMapping: Record<string, unknown>;
  lastAddedSource: unknown;
  projectById: Record<string, unknown>;
  flowOperationRequestFlags: Record<string, boolean>;
  metricsInterval: unknown;
  isMCPFlows: boolean;
}

function getMCPFlowsState(state: any): MCPFlowsState {
  const {
    app: {
      activeViews,
      accessControl,
      visibilityFilters: { filters } = {} as Record<string, unknown>,
      listing: { currentPage: listingCurrentPage, sortCriteria },
    },
    entities,
    sources: { lastAddedId: lastAddedSourceId },
    dataSets: { lastAddedId: lastAddedDataSetId },
    sinks: { lastAddedId: lastAddedSinkId },
    dataSetSchemas,
    request,
    routes,
    pipelines: pipelinesSlice,
  } = state;

  const lastAddedSource = state.entities.sources?.[lastAddedSourceId];
  const getSourceMeta = (flowNodeId: string) => {
    const {
      entities: { sources, vendors, flowNodes },
      paging: { metaInfoById },
    } = state;
    const sourceId = flowNodes[flowNodeId]?.data_source_id;
    const connectionOrVendorType = getConnectionOrVendorType(
      sources[sourceId],
      vendors,
      'source_type',
      'vendor_id',
    );
    if (connectionOrVendorType && has(metaInfoById, connectionOrVendorType)) {
      return metaInfoById[connectionOrVendorType];
    }
    return null;
  };

  const { currentPath, previousPath } = routes;
  const isAnotherPage = currentPath !== previousPath;

  const pipelines = memoizedMCPFlowsSelector(state);

  return {
    activeViews,
    accessControl,
    selectedFlow: state.hashedState.data?.flows?.selected,
    useExpanded: state.hashedState.data?.flows?.useExpanded,
    pipelines,
    filters: filters?.[SearchFilterViews.MCP_FLOWS] || [],
    rootNodeKeys: state.pipelines.rootNodeKeys,
    flowNodes: entities.flowNodes,
    listingPageForRequest: listingCurrentPage,
    currentPage: isAnotherPage ? 1 : listingCurrentPage,
    sortCriteria,
    lastAddedSourceId,
    lastAddedDataSetId,
    lastAddedSinkId,
    getSourceMeta,
    entities,
    dataSetSchemas,
    lastLoaded: pipelinesSlice.lastUpdated,
    isLoadingPipelines: !!request[GET_MCP_FLOWS_REQUEST] && pipelines.length === 0,
    isLoadingProjectById: false,
    loadingRemoveFromProject: !!request[REMOVE_FLOW_FROM_PROJECT_REQUEST],
    loadingDeleteProject: false,
    isRefreshingPipeline: !!request[REFRESH_PIPELINE_REQUEST],
    selectedCardID: pipelinesSlice.detailView?.id ?? null,
    projectSelectedFlow: null,
    projectDataFlowMapping: {},
    lastAddedSource,
    projectById: {},
    flowOperationRequestFlags: {
      [CLONE_FLOW_REQUEST]: request[CLONE_FLOW_REQUEST],
      [DELETE_PIPELINE_REQUEST]: request[DELETE_PIPELINE_REQUEST],
      [REMOVE_FLOW_FROM_PROJECT_REQUEST]: request[REMOVE_FLOW_FROM_PROJECT_REQUEST],
    },
    metricsInterval: getFlowMetricsInterval(state),
    isMCPFlows: true,
  };
}

const mapStateToProps = (state: any) => getMCPFlowsState(state);

const createMCPFlowsMapDispatch = () => ({
  saveHashedState: saveState,
  loadPipelines: loadMCPFlows,
  onLoadPipelinesSearchSuggestions: loadMCPFlowsSearchSuggestions,
  refreshPipeline,
  resetPageNumbers,
  setCurrentPage,
  setSortCriteria,
  resetSortCriteria,
  updateSearchFilters,
  setContextualSupport,
  setDetailViewCard,
  unsetContextualSupport,
  setTotalPages,
  setTotalItems,
  getCredentialsGroups,
  setCurrentProjectId: () => {},
  getProjectById: () => Promise.resolve(),
});

interface MCPFlowsWithMetricsAdapterProps {
  requestFlowMetrics: unknown;
  [key: string]: unknown;
}

function MCPFlowsWithMetricsAdapter(props: MCPFlowsWithMetricsAdapterProps) {
  const { requestFlowMetrics } = useMetricsAdapter();
  return <Pipelines {...props} requestFlowMetrics={requestFlowMetrics} />;
}

const MCPFlowsContainer: ComponentType = connect(
  mapStateToProps,
  createMCPFlowsMapDispatch(),
)(withRouter(MCPFlowsWithMetricsAdapter));

(MCPFlowsContainer as any).screenConfig = {
  screenName: 'MCPFlowsContainer',
  pageTitle: 'MCP Flows',
  domain: 'ai',
  modals: [ModalMoveToProject, ModalReingest, ModalFilePreview, ModalAddToMarketplace],
  path: [ROUTE_AI_MCP_FLOWS],
  exact: true,
  defaultProps: {
    withHeader: false,
    disableProjectView: true,
  },
};

export default MCPFlowsContainer;
